import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { setSafeProverbReturnUrl } from "../components/ReturnToProverb";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

const ERROR_MAP = {
  missing_token: "The handoff link was missing a token.",
  expired_token: "This handoff link has expired.",
  invalid_token: "This handoff link isn’t valid.",
  invalid_signature: "This handoff link couldn’t be verified.",
  wrong_source: "This handoff came from an unsupported source.",
  wrong_issuer: "This handoff came from an unrecognized issuer.",
  wrong_audience: "This handoff wasn’t intended for LifeGuide.",
  replayed_jti: "This handoff link has already been used.",
  lifetime_exceeded: "This handoff link is set to live too long.",
  missing_email: "The handoff was missing an email address.",
  missing_claim: "The handoff link was incomplete.",
  rate_limited: "Too many attempts. Please wait a moment and try again.",
  sso_not_configured: "SSO isn’t configured yet on LifeGuide.",
};

export default function SSOPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "checking" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = params.get("token");
    if (!token) {
      // Strip URL then send to friendly error
      window.history.replaceState({}, "", "/sso/error?reason=missing_token");
      navigate("/sso/error?reason=missing_token", { replace: true });
      return;
    }

    const exchange = async () => {
      try {
        const resp = await fetch(`${API}/sso/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        // Immediately strip the token from the URL so it can't leak
        window.history.replaceState({}, "", "/sso");
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const reason = data?.detail?.error || data?.detail || "invalid_token";
          navigate(`/sso/error?reason=${encodeURIComponent(reason)}`, { replace: true });
          return;
        }
        localStorage.setItem("lg_token", data.token);
        setUser(data.user);
        toast.success(data.welcome || "Welcome from I Need a Proverb.");
        setState({ status: "ok" });

        // Persist the "Return to I Need a Proverb" URL for the pill link.
        // setSafeProverbReturnUrl silently ignores anything not on the
        // ineedaproverb.com host allowlist, so the client can never be
        // pushed to an untrusted host.
        setSafeProverbReturnUrl(data.return_url);

        // Deep-link honor: if the partner sent feature="say-it-better",
        // drop the user straight into that feature — no dashboard detour.
        // Any unknown value is ignored by the backend before it gets here,
        // so this switch only ever sees allowlisted values.
        const dest = data.feature === "say-it-better" ? "/analyze" : "/";
        setTimeout(() => navigate(dest, { replace: true }), 900);
      } catch (err) {
        navigate(`/sso/error?reason=server_error`, { replace: true });
      }
    };
    exchange();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center fade-in-up">
        {state.status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Loader2 size={28} className="animate-spin" />
            </div>
            <h1 className="font-display text-2xl text-[#2E2E2E] mb-2">
              Bringing you into LifeGuide…
            </h1>
            <p className="text-[#6B6B6B] text-sm leading-relaxed">
              Just a moment while we verify your handoff.
            </p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Check size={32} strokeWidth={2.2} />
            </div>
            <h1 className="font-display text-3xl text-[#2E2E2E] mb-2">Welcome home</h1>
            <p className="text-[#6B6B6B] text-sm">Opening your dashboard…</p>
          </>
        )}
      </div>
    </div>
  );
}

export { ERROR_MAP };
